<div class="question-card" tabindex="0" role="group" [attr.aria-labelledby]="cardHeadingId">
    <!-- HEADER: lead-in (the card title) + step pager. No X-close, no
         chevron icons up here — per spec the only header content is the
         title and "X of Y" in the right rail. Required questions still
         block closing; that affordance lives in the parent drawer's
         X button, not on the card. -->
    <div class="card-header">
        <h3 class="lead-in" [id]="cardHeadingId">{{ batch?.leadIn }}</h3>
        <span class="pager">{{ displayIndex }} of {{ totalQuestions }}</span>
    </div>

    <ng-container *ngIf="currentQuestion as q">
        <div class="prompt-row">
            <span class="prompt-label">
                {{ q.prompt }}<span class="required-asterisk" *ngIf="q.required" aria-hidden="true">*</span>
            </span>
            <span class="required-flag" *ngIf="q.required">Required</span>
        </div>
        <p class="context" *ngIf="q.context">{{ q.context }}</p>

        <!-- BODY -->
        <div class="card-body" [ngSwitch]="effectiveKind(q)">
            <!-- SINGLE_SELECT -->
            <ng-container *ngSwitchCase="'single_select'">
                <ng-container *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue">
                    <button
                        type="button"
                        class="option-row"
                        [class.is-selected]="isOptionSelected(q, opt)"
                        [class.is-focused]="focusedOptionIndex === i"
                        [disableRipple]="true"
                        (click)="onSingleSelectClick(q, opt); next()"
                        (mouseenter)="focusedOptionIndex = i">
                        <span class="row-num" *ngIf="i < 9">{{ i + 1 }}</span>
                        <span class="row-body">
                            <span class="row-label">{{ opt.label }}</span>
                            <span class="row-hint" *ngIf="opt.hint">{{ opt.hint }}</span>
                        </span>
                        <span class="recommended-badge" *ngIf="opt.recommended">Recommended</span>
                    </button>
                </ng-container>
                <ng-container *ngIf="q.allow_other">
                    <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                </ng-container>
            </ng-container>

            <!-- MULTI_SELECT -->
            <ng-container *ngSwitchCase="'multi_select'">
                <ng-container *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue">
                    <button
                        type="button"
                        class="option-row multi"
                        [class.is-selected]="isOptionSelected(q, opt)"
                        [class.is-focused]="focusedOptionIndex === i"
                        [disableRipple]="true"
                        (click)="onMultiSelectToggle(q, opt)"
                        (mouseenter)="focusedOptionIndex = i">
                        <span class="checkbox" aria-hidden="true">
                            <mat-icon
                                *ngIf="isOptionSelected(q, opt)"
                                svgIcon="icon-v2:check"></mat-icon>
                        </span>
                        <span class="row-body">
                            <span class="row-label">{{ opt.label }}</span>
                            <span class="row-hint" *ngIf="opt.hint">{{ opt.hint }}</span>
                        </span>
                        <span class="recommended-badge" *ngIf="opt.recommended">Recommended</span>
                    </button>
                </ng-container>
                <ng-container *ngIf="q.allow_other">
                    <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                </ng-container>
                <p class="multi-hint" *ngIf="multiSelectionCount(q) > 0">
                    {{ multiSelectionCount(q) }} selected — Enter to confirm and continue
                </p>
            </ng-container>

            <!-- TEXT (also fallback for unknown kinds + *_select with no options) -->
            <ng-container *ngSwitchCase="'text'">
                <input
                    #freeFormInput
                    type="text"
                    class="text-input"
                    [placeholder]="q.prompt"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>

            <!-- TEXTAREA — multi-line free-form (email bodies, notes, descriptions) -->
            <ng-container *ngSwitchCase="'textarea'">
                <textarea
                    #freeFormInput
                    class="text-input textarea-input"
                    rows="4"
                    [placeholder]="q.prompt"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()"></textarea>
            </ng-container>

            <!-- NUMBER -->
            <ng-container *ngSwitchCase="'number'">
                <input
                    #freeFormInput
                    type="number"
                    class="text-input"
                    [placeholder]="q.prompt"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>

            <!-- DATE -->
            <ng-container *ngSwitchCase="'date'">
                <input
                    type="date"
                    class="text-input"
                    [placeholder]="q.prompt"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>

            <!-- UNIT PICKER (inline, same selector used by Custom Reports V2) -->
            <ng-container *ngSwitchCase="'unit_picker'">
                <div class="inline-unit-selector">
                    <app-v2-unit-selector
                        [selectionMode]="true"
                        [preselectedUnitIds]="pickedUnitIdsFor(q)"
                        [initialFilterPanelOpen]="false"
                        (onSelect)="onUnitSelectionChanged(q, $event)">
                    </app-v2-unit-selector>
                </div>
                <p class="picker-count" *ngIf="pickedUnitsFor(q).length > 0">
                    {{ pickedUnitsFor(q).length }} selected
                </p>
            </ng-container>

            <!-- STUBBED PICKERS (vendor, member, owner, etc.) -->
            <ng-container *ngSwitchCase="'picker_stub'">
                <div class="picker-stub">
                    <i class="ph-bold ph-info icon-muted icon-20" aria-hidden="true"></i>
                    <span>The {{ pickerEntityLabel(q) }} picker isn't wired up yet.
                        Skip this question and Gable will fall back to asking another way.</span>
                </div>
            </ng-container>
        </div>

        <!-- FOOTER: V2 buttons, justify-between. White accent Back (with
             leading Phosphor arrow) on the left; primary blue Next/Submit
             on the right. "Next" gets a trailing arrow via the V2
             matButtonIconSuffix directive — Submit doesn't.
             Per UI_V2_BUTTONS.md: always mat-flat-button (never stroked),
             color="accent" for white, color="primary" for blue. -->
        <div class="card-footer">
            <button
                type="button"
                mat-flat-button
                color="accent"
                [disableRipple]="true"
                class="footer-btn footer-btn-back"
                [disabled]="currentIndex === 0 || submitting"
                (click)="prev()"
                aria-label="Previous question">
                <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
                Back
            </button>
            <button
                *ngIf="!isLastQuestion"
                type="button"
                mat-flat-button
                color="primary"
                [disableRipple]="true"
                matButtonIconSuffix
                class="footer-btn footer-btn-next"
                [disabled]="!canAdvance || submitting"
                (click)="next()"
                aria-label="Next question">
                Next
                <i class="ph-bold ph-arrow-right icon-16" aria-hidden="true"></i>
            </button>
            <button
                *ngIf="isLastQuestion"
                type="button"
                mat-flat-button
                color="primary"
                [disableRipple]="true"
                class="footer-btn footer-btn-submit"
                [disabled]="!canAdvance || submitting"
                (click)="next()"
                aria-label="Submit answers">
                Submit
            </button>
        </div>
    </ng-container>
</div>

<!-- Reusable "Something else" row used by both single & multi select -->
<ng-template #otherRow let-q="q">
    <div
        class="option-row other-row"
        [class.is-active]="state[q.id]?.otherActive"
        [class.is-focused]="isOtherRowFocused(q)"
        (mouseenter)="focusedOptionIndex = (q.options?.length ?? 0)">
        <span class="row-num" aria-hidden="true">+</span>
        <ng-container *ngIf="!state[q.id]?.otherActive">
            <button
                type="button"
                class="other-trigger"
                [disableRipple]="true"
                (click)="onActivateOther(q)">
                Something else…
            </button>
        </ng-container>
        <input
            *ngIf="state[q.id]?.otherActive"
            #freeFormInput
            type="text"
            class="other-input"
            placeholder="Type your answer"
            [ngModel]="state[q.id]?.otherText ?? ''"
            (ngModelChange)="onOtherTextInput(q, $event)"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()" />
    </div>
</ng-template>
